import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase, POSTS_TABLE, STORAGE_BUCKET, slugify } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import Seo from '../../components/Seo';
import {
  ArrowLeft, Save, Send, Upload, X, Image as ImageIcon,
  AlertCircle, CheckCircle2, Loader2, ExternalLink, Trash2,
} from 'lucide-react';

const EMPTY = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  cover_url: '',
  tags: [],
  status: 'draft',
  published_at: null,
};

export default function AdminPostEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [autoSlug, setAutoSlug] = useState(isNew);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.from(POSTS_TABLE).select('*').eq('id', id).single();
      if (error) setError(error.message);
      else if (data) {
        setForm({
          title: data.title || '',
          slug: data.slug || '',
          excerpt: data.excerpt || '',
          content: data.content || '',
          cover_url: data.cover_url || '',
          tags: data.tags || [],
          status: data.status || 'draft',
          published_at: data.published_at || null,
        });
        setTagsInput((data.tags || []).join(', '));
      }
      setLoading(false);
    })();
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (e) => {
    const t = e.target.value;
    set('title', t);
    if (autoSlug) set('slug', slugify(t));
  };

  const onSlugChange = (e) => {
    setAutoSlug(false);
    set('slug', slugify(e.target.value));
  };

  const onTagsBlur = () => {
    const arr = tagsInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    set('tags', arr);
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    if (file.size > 8 * 1024 * 1024) { setError('Image must be under 8MB.'); return; }
    setUploading(true);
    setError('');
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
      const path = `covers/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
        cacheControl: '31536000',
        upsert: false,
        contentType: file.type,
      });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
      set('cover_url', data.publicUrl);
      setNotice('Image uploaded.');
    } catch (e) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const save = async ({ publish = null } = {}) => {
    setError('');
    setNotice('');
    if (!form.title.trim()) { setError('Please add a title.'); return; }
    if (!form.slug.trim()) { setError('Please add a URL slug.'); return; }
    setSaving(true);

    // finalize tags in case user hasn't blurred yet
    const tags = tagsInput.split(',').map((s) => s.trim()).filter(Boolean);
    let nextStatus = form.status;
    let publishedAt = form.published_at;
    if (publish === true) { nextStatus = 'published'; if (!publishedAt) publishedAt = new Date().toISOString(); }
    if (publish === false) { nextStatus = 'draft'; }

    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_url: form.cover_url || null,
      tags,
      status: nextStatus,
      published_at: publishedAt,
      author_email: user?.email || null,
    };

    try {
      if (isNew) {
        const { data, error } = await supabase.from(POSTS_TABLE).insert(payload).select().single();
        if (error) throw error;
        navigate(`/admin/posts/${data.id}/edit`, { replace: true });
        setNotice(nextStatus === 'published' ? 'Post published.' : 'Draft saved.');
      } else {
        const { error } = await supabase.from(POSTS_TABLE).update(payload).eq('id', id);
        if (error) throw error;
        setForm((f) => ({ ...f, status: nextStatus, published_at: publishedAt, tags }));
        setNotice(nextStatus === 'published' ? 'Post published.' : publish === false ? 'Post unpublished.' : 'Saved.');
      }
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const removeCover = () => set('cover_url', '');

  if (loading) return <div className="p-16 text-center text-[color:var(--taupe-gray)]">Loading…</div>;

  return (
    <>
      <Seo title={isNew ? 'New Post' : 'Edit Post'} description="Admin blog editor." path="/admin" />
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[color:var(--taupe-gray)] hover:text-[color:var(--dusty-rose)] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>

          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <div>
              <div className="eyebrow mb-1">{isNew ? 'Create' : 'Edit'}</div>
              <h1 className="font-serif-display text-3xl text-[color:var(--deep-sage)]">{isNew ? 'New blog post' : form.title || 'Untitled'}</h1>
              {!isNew && form.status === 'published' && form.slug && (
                <Link to={`/blog/${form.slug}`} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--dusty-rose)] hover:underline inline-flex items-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" /> View live: /blog/{form.slug}
                </Link>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => save({ publish: false })} disabled={saving} className="btn-secondary text-sm disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save draft
              </button>
              {form.status === 'published' ? (
                <button onClick={() => save({ publish: false })} disabled={saving} className="btn-secondary text-sm disabled:opacity-60">
                  Unpublish
                </button>
              ) : null}
              <button onClick={() => save({ publish: true })} disabled={saving} className="btn-primary text-sm disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {form.status === 'published' ? 'Save & keep published' : 'Publish'}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-xl px-4 py-3 mb-4 text-sm flex items-start gap-2" style={{ background: 'rgba(232,165,169,0.15)', color: '#8B3A44' }}>
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
          {notice && (
            <div className="rounded-xl px-4 py-3 mb-4 text-sm flex items-start gap-2" style={{ background: 'rgba(184,190,164,0.20)', color: 'var(--sage-dark)' }}>
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> {notice}
            </div>
          )}

          <div className="grid lg:grid-cols-[1fr_320px] gap-6">
            {/* Main editor */}
            <div className="space-y-5">
              <div className="card-soft p-5">
                <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="post-title">Title</label>
                <input
                  id="post-title"
                  value={form.title}
                  onChange={onTitleChange}
                  placeholder="A hopeful, engaging title"
                  className="mt-2 w-full text-2xl font-serif-display text-[color:var(--deep-sage)] bg-transparent focus:outline-none border-b pb-2"
                  style={{ borderColor: 'var(--soft-border)' }}
                />

                <div className="mt-4">
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="post-slug">
                    URL slug
                    {autoSlug && <span className="ml-2 text-[10px] normal-case tracking-normal font-normal text-[color:var(--taupe-gray)] italic">auto-generated</span>}
                  </label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-xs text-[color:var(--taupe-gray)]">/blog/</span>
                    <input
                      id="post-slug"
                      value={form.slug}
                      onChange={onSlugChange}
                      placeholder="post-url-slug"
                      className="flex-1 rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)]"
                      style={{ borderColor: 'var(--soft-border)' }}
                    />
                  </div>
                </div>
              </div>

              <div className="card-soft p-5">
                <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="post-excerpt">Excerpt</label>
                <textarea
                  id="post-excerpt"
                  value={form.excerpt}
                  onChange={(e) => set('excerpt', e.target.value)}
                  rows={3}
                  placeholder="A 1–2 sentence summary shown on the blog card and in search previews."
                  className="mt-2 w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)]"
                  style={{ borderColor: 'var(--soft-border)' }}
                />
              </div>

              <div className="card-soft p-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold" htmlFor="post-content">Content</label>
                  <span className="text-[10px] text-[color:var(--taupe-gray)] italic">Plain text or Markdown — supports **bold**, *italic*, [links](url), and paragraph breaks.</span>
                </div>
                <textarea
                  id="post-content"
                  value={form.content}
                  onChange={(e) => set('content', e.target.value)}
                  rows={20}
                  placeholder={"Start writing your post…\n\nA new line = new paragraph.\n\nUse **bold** or *italic* for emphasis, and [link text](https://example.com) for links."}
                  className="w-full rounded-lg border bg-white px-3 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)] font-mono"
                  style={{ borderColor: 'var(--soft-border)' }}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              <div className="card-soft p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold mb-3">Featured image</div>
                {form.cover_url ? (
                  <div className="space-y-2">
                    <img src={form.cover_url} alt="Cover preview" className="w-full aspect-[16/10] object-cover rounded-lg" />
                    <div className="flex gap-2">
                      <button onClick={() => fileRef.current?.click()} className="btn-secondary text-xs flex-1 justify-center">
                        <Upload className="w-3.5 h-3.5" /> Replace
                      </button>
                      <button onClick={removeCover} className="btn-secondary text-xs justify-center" title="Remove image">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-full aspect-[16/10] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-[color:var(--taupe-gray)] hover:text-[color:var(--dusty-rose)] hover:border-[color:var(--dusty-rose)] transition"
                    style={{ borderColor: 'var(--soft-border)' }}
                  >
                    {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImageIcon className="w-6 h-6" />}
                    <span className="text-xs uppercase tracking-widest font-semibold">{uploading ? 'Uploading…' : 'Upload image'}</span>
                    <span className="text-[10px] normal-case">JPG, PNG, or WebP · up to 8MB</span>
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(f); e.target.value = ''; }}
                />
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--taupe-gray)] font-semibold">Or paste an image URL</label>
                  <input
                    value={form.cover_url}
                    onChange={(e) => set('cover_url', e.target.value)}
                    placeholder="https://…"
                    className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)]"
                    style={{ borderColor: 'var(--soft-border)' }}
                  />
                </div>
              </div>

              <div className="card-soft p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold mb-3">Tags</div>
                <input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  onBlur={onTagsBlur}
                  placeholder="anxiety, trauma, women…"
                  className="w-full rounded-lg border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--blush-pink)]"
                  style={{ borderColor: 'var(--soft-border)' }}
                />
                <div className="text-[10px] text-[color:var(--taupe-gray)] mt-1.5">Comma-separated. Tags help visitors filter the blog.</div>
              </div>

              <div className="card-soft p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--sage-dark)] font-semibold mb-3">Status</div>
                <div className="flex items-center gap-2">
                  {form.status === 'published' ? (
                    <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: 'rgba(184,190,164,0.28)', color: 'var(--sage-dark)' }}>Published</span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: 'rgba(232,165,169,0.20)', color: 'var(--dusty-rose)' }}>Draft</span>
                  )}
                </div>
                {form.published_at && (
                  <div className="text-[10px] text-[color:var(--taupe-gray)] mt-2">
                    First published {new Date(form.published_at).toLocaleDateString()}
                  </div>
                )}
              </div>

              {!isNew && (
                <button
                  onClick={async () => {
                    if (!window.confirm('Delete this post permanently?')) return;
                    const { error } = await supabase.from(POSTS_TABLE).delete().eq('id', id);
                    if (error) setError(error.message);
                    else navigate('/admin');
                  }}
                  className="w-full text-sm text-[color:var(--dusty-rose)] hover:underline inline-flex items-center justify-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" /> Delete post
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
